import React, { useState, useEffect } from 'react';
import { X, DollarSign, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { recordOrderPayment } from '../../api/wholesale/orders';

/**
 * PaymentRecordingModal
 * Modal for recording payments on wholesale orders
 * Supports: Full Payment and Partial Payment
 */
const PaymentRecordingModal = ({ isOpen, onClose, onSuccess, order }) => {
  const [formData, setFormData] = useState({
    payment_method: 'full_payment',
    amount_paid: '',
    outstanding_amount: 0,
    payment_notes: ''
  });
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && order) {
      const totalAmount = parseFloat(order.total_amount) || 0;
      setFormData({
        payment_method: 'full_payment',
        amount_paid: totalAmount,
        outstanding_amount: 0,
        payment_notes: ''
      });
    }
  }, [isOpen, order]);

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, loading]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const totalAmount = parseFloat(order?.total_amount) || 0;

    if (name === 'payment_method') {
      // When switching payment method
      if (value === 'full_payment') {
        setFormData(prev => ({
          ...prev,
          payment_method: value,
          amount_paid: totalAmount,
          outstanding_amount: 0
        }));
      } else {
        // Partial payment - clear amount for user to enter
        setFormData(prev => ({
          ...prev,
          payment_method: value,
          amount_paid: '',
          outstanding_amount: totalAmount
        }));
      }
    } else if (name === 'amount_paid') {
      const amountPaid = value === '' ? '' : Number(value);
      const outstanding = amountPaid === '' ? totalAmount : Math.max(0, totalAmount - amountPaid);
      setFormData(prev => ({
        ...prev,
        amount_paid: amountPaid,
        outstanding_amount: outstanding
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
      }));
    }
  };

  // Calculate payment status
  const getPaymentStatus = () => {
    if (!order) return null;

    const totalAmount = parseFloat(order.total_amount) || 0;
    const amountPaid = parseFloat(formData.amount_paid) || 0;

    if (formData.payment_method === 'full_payment') {
      return {
        type: 'success',
        message: 'Full payment - Receipt will be generated'
      };
    }

    if (amountPaid === 0) {
      return {
        type: 'error',
        message: 'Please enter payment amount'
      };
    }

    if (amountPaid >= totalAmount) {
      return {
        type: 'info',
        message: 'Amount paid covers full order - consider using Full Payment option'
      };
    }

    const outstanding = totalAmount - amountPaid;
    return {
      type: 'warning',
      message: `Partial payment - Invoice will be generated with ₦${outstanding.toLocaleString()} outstanding`
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!order) {
      toast.error('Order information is missing');
      return;
    }

    const totalAmount = parseFloat(order.total_amount) || 0;
    const amountPaid = parseFloat(formData.amount_paid) || 0;

    // Validation
    if (formData.payment_method === 'partial_payment') {
      if (amountPaid <= 0) {
        toast.error('Please enter payment amount');
        return;
      }
      if (amountPaid >= totalAmount) {
        toast.error('For full payment, please select "Full Payment" option');
        return;
      }
    }

    setLoading(true);

    try {
      const payload = {
        payment_method: formData.payment_method === 'full_payment' ? 'bank_transfer' : 'bank_transfer',
        payment_type: formData.payment_method, // 'full_payment' or 'partial_payment'
        amount_paid: amountPaid,
        outstanding_amount: formData.outstanding_amount,
        credit_amount_used: 0,
        payment_notes: formData.payment_notes || null
      };

      const response = await recordOrderPayment(order.id, payload);

      if (formData.payment_method === 'full_payment') {
        toast.success('Full payment recorded - Receipt generated');
      } else {
        toast.success('Partial payment recorded - Invoice generated');
      }

      onSuccess(response.data);
      handleClose();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !order) return null;

  const paymentStatus = getPaymentStatus();
  const totalAmount = parseFloat(order.total_amount) || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-secondary-lightGray px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-primary-black flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-accent-gold" />
              Record Payment
            </h2>
            <p className="text-sm text-secondary-gray mt-1">
              Order #{order.order_number} - {order.distributor_business_name || order.distributor?.business_name}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-secondary-gray hover:text-primary-black transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Order Summary */}
          <div className="bg-secondary-offWhite rounded-lg p-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <p className="text-sm text-secondary-gray">Total Order Amount</p>
                <p className="text-2xl font-bold text-primary-black">
                  ₦{totalAmount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-primary-black mb-2">
              Payment Method <span className="text-red-500">*</span>
            </label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              disabled={loading}
              className="w-full px-4 py-2.5 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none disabled:bg-gray-100"
            >
              <option value="full_payment">Full Payment</option>
              <option value="partial_payment">Partial Payment</option>
            </select>
          </div>

          {/* Amount Paid Field */}
          <div>
            <label className="block text-sm font-medium text-primary-black mb-2">
              Amount Paid (₦) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amount_paid"
              value={formData.amount_paid}
              onChange={handleChange}
              disabled={loading || formData.payment_method === 'full_payment'}
              step="0.01"
              min="0"
              max={formData.payment_method === 'partial_payment' ? totalAmount - 1 : undefined}
              className={`w-full px-4 py-2.5 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none ${
                formData.payment_method === 'full_payment' ? 'bg-gray-100 cursor-not-allowed' : ''
              } disabled:bg-gray-100`}
              placeholder="Enter amount paid"
            />
            {formData.payment_method === 'full_payment' && (
              <p className="text-xs text-green-600 mt-1">
                Full order amount will be recorded as paid
              </p>
            )}
          </div>

          {/* Outstanding Amount Field - Only for Partial Payment */}
          {formData.payment_method === 'partial_payment' && (
            <div>
              <label className="block text-sm font-medium text-primary-black mb-2">
                Outstanding Amount (₦)
              </label>
              <input
                type="number"
                name="outstanding_amount"
                value={formData.outstanding_amount}
                disabled
                className="w-full px-4 py-2.5 border border-secondary-lightGray rounded-lg bg-gray-100 cursor-not-allowed"
                placeholder="Auto-calculated"
              />
              <p className="text-xs text-orange-600 mt-1">
                This amount will be shown as balance due on the invoice
              </p>
            </div>
          )}

          {/* Payment Notes */}
          <div>
            <label className="block text-sm font-medium text-primary-black mb-2">
              Payment Notes (Optional)
            </label>
            <textarea
              name="payment_notes"
              value={formData.payment_notes}
              onChange={handleChange}
              disabled={loading}
              rows={2}
              className="w-full px-4 py-2.5 border border-secondary-lightGray rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent outline-none resize-none disabled:bg-gray-100"
              placeholder="Additional notes about this payment..."
            />
          </div>

          {/* Payment Status Indicator */}
          {paymentStatus && (
            <div
              className={`flex items-start gap-3 p-4 rounded-lg ${
                paymentStatus.type === 'success'
                  ? 'bg-green-50 border border-green-200'
                  : paymentStatus.type === 'warning'
                  ? 'bg-yellow-50 border border-yellow-200'
                  : paymentStatus.type === 'error'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-blue-50 border border-blue-200'
              }`}
            >
              {paymentStatus.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle
                  className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    paymentStatus.type === 'warning'
                      ? 'text-yellow-600'
                      : paymentStatus.type === 'error'
                      ? 'text-red-600'
                      : 'text-blue-600'
                  }`}
                />
              )}
              <div>
                <p
                  className={`text-sm font-medium ${
                    paymentStatus.type === 'success'
                      ? 'text-green-800'
                      : paymentStatus.type === 'warning'
                      ? 'text-yellow-800'
                      : paymentStatus.type === 'error'
                      ? 'text-red-800'
                      : 'text-blue-800'
                  }`}
                >
                  {paymentStatus.message}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-secondary-lightGray">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 px-6 py-2.5 border border-secondary-lightGray text-secondary-gray rounded-lg hover:bg-secondary-offWhite transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || paymentStatus?.type === 'error'}
              className="flex-1 px-6 py-2.5 bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Recording...</span>
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  <span>Record Payment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentRecordingModal;
